import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import RequiredApiNotice from './RequiredApiNotice';
import EmploymentGrowthSection from './EmploymentGrowthPanel';

interface SwingsDashboardProps {
  data: any;
  address: any;
}

const SWING_COLORS = ['#1e88e5', '#e53935', '#43a047', '#fb8c00', '#8e24aa', '#00acc1', '#f4511e', '#3949ab'];

const SwingsDashboard: React.FC<SwingsDashboardProps> = ({ data }) => {
  const dashboardData = data?.data && !data?.incomeTrend && !data?.historicalPopulation
    ? data.data
    : data;

  if (!dashboardData || dashboardData.error) {
    return (
      <div>
        <div>Data not available</div>
        <RequiredApiNotice apis={['Zillow ZHVI/ZORI API', 'FHFA HPI API', 'US Census historical series']} />
      </div>
    );
  }

  const buildTrendSeries = (trend: any) => {
    if (!trend?.years || !trend?.datasets) return [];
    return trend.years.map((year: number | string, index: number) => {
      const point: any = { year };
      for (const ds of trend.datasets) point[ds.label] = ds.data[index];
      return point;
    });
  };

  const incomeTrendSeries = buildTrendSeries(dashboardData.incomeTrend);
  const ownerValueSeries = buildTrendSeries(dashboardData.ownerValueTrend);
  const renterRentSeries = buildTrendSeries(dashboardData.renterRentTrend);

  const historical = dashboardData.historicalPopulation || {};
  const popLabels = Object.keys(historical).sort();
  const popValues = popLabels.map((y: string) => Number(historical[y]) || 0);
  const baseCurrent = popValues[popValues.length - 1] || 0;
  const populationTrendSeries = [
    ...popLabels.map((label: string, i: number) => ({ label, value: popValues[i] })),
    { label: 'Current', value: baseCurrent },
    { label: '5 Yr. Proj', value: Math.round(baseCurrent * 1.02) },
  ].filter((x) => x.value > 0);
  const householdTrendSeries = populationTrendSeries.map((p) => ({ label: p.label, value: Math.round(p.value / 2.5) }));

  const missingFields: string[] = [];
  if (incomeTrendSeries.length === 0) missingFields.push('Household Income Trend');
  if (populationTrendSeries.length === 0) missingFields.push('Population Trend');
  if (ownerValueSeries.length === 0) missingFields.push('Owner-Occupied Value Trend');
  if (renterRentSeries.length === 0) missingFields.push('Renter Rent Trend');
  if (!dashboardData.metroEmploymentGrowth) missingFields.push('Metro Job Growth');
  if (!dashboardData.neighborhoodEmploymentGrowth) missingFields.push('Neighborhood Job Growth');

  const employmentApis =
    !dashboardData.metroEmploymentGrowth && !dashboardData.neighborhoodEmploymentGrowth
      ? ['BLS CES (optional BLS_API_KEY)', 'Census LODES WAC']
      : !dashboardData.metroEmploymentGrowth
        ? ['BLS CES (optional BLS_API_KEY)']
        : !dashboardData.neighborhoodEmploymentGrowth
          ? ['Census LODES WAC', 'Census tract FIPS']
          : [];

  return (
    <div>
      <h2>S - SWINGS & TRENDS</h2>
      <p style={{ color: '#666', marginBottom: '20px' }}>
        Is this market stable, growing, or volatile?
      </p>

      {(dashboardData.metroEmploymentGrowth || dashboardData.neighborhoodEmploymentGrowth) && (
        <div className="card emp-panel">
          <h3 style={{ marginTop: 0 }}>Employment growth</h3>
          <p style={{ color: '#64748b', fontSize: 13, marginTop: -4, marginBottom: 18 }}>
            Metro monthly CES and neighborhood annual LODES workplace jobs — level over time with jobs added/lost.
          </p>
          {dashboardData.metroEmploymentGrowth && (
            <EmploymentGrowthSection data={dashboardData.metroEmploymentGrowth} />
          )}
          {dashboardData.neighborhoodEmploymentGrowth && (
            <EmploymentGrowthSection data={dashboardData.neighborhoodEmploymentGrowth} />
          )}
        </div>
      )}

      <div className="card">
        <h3>Household income trend</h3>
        {incomeTrendSeries.length > 0 ? (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={incomeTrendSeries}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="year" />
              <YAxis tickFormatter={(v) => `$${Number(v).toLocaleString()}`} />
              <Tooltip formatter={(v: any) => `$${Number(v).toLocaleString()}`} />
              <Legend />
              {dashboardData.incomeTrend?.datasets?.map((ds: any, index: number) => (
                <Line key={ds.label} type="monotone" dataKey={ds.label} strokeWidth={2} stroke={SWING_COLORS[index % SWING_COLORS.length]} dot={{ r: 3 }} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p style={{ color: '#6b7280', fontSize: 13 }}>Data not available.</p>
        )}
      </div>

      <div className="card">
        <h3>Population trend</h3>
        {populationTrendSeries.length > 0 ? (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={populationTrendSeries}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" />
              <YAxis />
              <Tooltip formatter={(v: any) => Number(v).toLocaleString()} />
              <Bar dataKey="value" fill="#0288d1" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p style={{ color: '#6b7280', fontSize: 13 }}>Data not available.</p>
        )}
      </div>

      <div className="card">
        <h3>Number of households trend</h3>
        {householdTrendSeries.length > 0 ? (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={householdTrendSeries}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" />
              <YAxis />
              <Tooltip formatter={(v: any) => Number(v).toLocaleString()} />
              <Bar dataKey="value" fill="#00897b" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p style={{ color: '#6b7280', fontSize: 13 }}>Data not available.</p>
        )}
      </div>

      <div className="card">
        <h3># of owner-occupied houses by value</h3>
        {ownerValueSeries.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={ownerValueSeries}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="year" />
              <YAxis />
              <Tooltip formatter={(v: any) => Number(v).toLocaleString()} />
              <Legend />
              {dashboardData.ownerValueTrend.datasets.map((ds: any, index: number) => (
                <Bar key={ds.label} dataKey={ds.label} stackId="owner" fill={SWING_COLORS[index % SWING_COLORS.length]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p style={{ color: '#6b7280', fontSize: 13 }}>Data not available.</p>
        )}
      </div>

      <div className="card">
        <h3># of renter occupied housing units by gross rent</h3>
        {renterRentSeries.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={renterRentSeries}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="year" />
              <YAxis />
              <Tooltip formatter={(v: any) => Number(v).toLocaleString()} />
              <Legend />
              {dashboardData.renterRentTrend.datasets.map((ds: any, index: number) => (
                <Bar key={ds.label} dataKey={ds.label} stackId="renter" fill={SWING_COLORS[index % SWING_COLORS.length]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p style={{ color: '#6b7280', fontSize: 13 }}>Data not available.</p>
        )}
      </div>

      {missingFields.length > 0 && (
        <RequiredApiNotice
          apis={[
            ...employmentApis,
            'Zillow ZHVI/ZORI API',
            'FHFA HPI API',
            'US Census historical series',
          ].filter((v, i, arr) => arr.indexOf(v) === i)}
          missingFields={missingFields}
        />
      )}
    </div>
  );
};

export default SwingsDashboard;
